import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
} from "firebase/firestore";
import {
  BroBotMemory,
  Championship,
  ChampionshipTeam,
  ChatGroup,
  ChatMessage,
  CockInHoleGame,
  CoinEvent,
  InventoryItem,
  ItemState,
  LastKnownLocation,
  LottoDraw,
  LedgerEntry,
  Morsal,
  Puzzle,
  PuzzleEntry,
  Quote,
  QuoteRouletteSession,
  Session,
  Spite,
  SpiteEvent,
  SpiteGuard,
  Sweepstake,
  SweepstakeTeam,
  ThumbEvent,
  Tontine,
  UserProfile,
  Venue,
  YearAwards,
} from "shared/types";

import { app } from "./app";
import { collection, doc } from "./utils";

// Use memory cache + long-polling when running against the emulator.
// Long-polling avoids WebSocket stream state assertion failures that occur in
// development (HMR) when the auth state changes and Firestore tries to restart
// its listeners on a partially-torn-down watch stream.
export const db =
  import.meta.env.VITE_USE_EMULATORS === "true"
    ? initializeFirestore(app, {
        localCache: memoryLocalCache(),
        experimentalForceLongPolling: true,
      })
    : initializeFirestore(app, {});

if (import.meta.env.VITE_USE_EMULATORS === "true") {
  connectFirestoreEmulator(db, "localhost", 8082);
}

export const profilesRef = collection<UserProfile>("/profiles");
export const getProfileRef = (id: string) => doc<UserProfile>(`/profiles/${id}`);

// Only bros can read these (see firestore.rules)
export const lastKnownLocationsRef = collection<LastKnownLocation>("/lastKnownLocations");
export const getLastKnownLocationRef = (profileId: string) =>
  doc<LastKnownLocation>(`/lastKnownLocations/${profileId}`);

export const venuesRef = collection<Venue>("/venues");
export const getVenueRef = (id: string) => doc<Venue>(`/venues/${id}`);

export const sessionsRef = collection<Session>("/sessions");
export const getSessionRef = (id: string) => doc<Session>(`/sessions/${id}`);

export const getInventoryRef = (profileId: string) =>
  collection<InventoryItem>(`/profiles/${profileId}/inventory`);
export const getInventoryItemRef = (profileId: string, id: string) =>
  doc<InventoryItem>(`/profiles/${profileId}/inventory/${id}`);

export const itemStateRef = collection<ItemState>("/itemState");
export const getItemStateRef = (id: string) => doc<ItemState>(`/itemState/${id}`);
export const lottoDrawsRef = collection<LottoDraw>("/lottoDraws");
export const getLottoDrawRef = (key: string) => doc<LottoDraw>(`/lottoDraws/${key}`);

export const spitesRef = collection<Spite>("/spites");
export const getSpiteRef = (id: string) => doc<Spite>(`/spites/${id}`);
export const getSpiteGuardRef = (profileId: string) => doc<SpiteGuard>(`/spiteGuards/${profileId}`);
export const spiteEventsRef = collection<SpiteEvent>("/spiteEvents");

export const tontinesRef = collection<Tontine>("/tontines");

export const thumbEventsRef = collection<ThumbEvent>("/thumbEvents");
export const getThumbEventRef = (id: string) => doc<ThumbEvent>(`/thumbEvents/${id}`);

export const getCockInHoleGameRef = (profileId: string) =>
  doc<CockInHoleGame>(`/cockInHoleGames/${profileId}`);

export const puzzlesRef = collection<Puzzle>("/puzzles");
export const getPuzzleRef = (id: string) => doc<Puzzle>(`/puzzles/${id}`);
// Only you can read your own (see firestore.rules).
export const getPuzzleEntryRef = (puzzleId: string, profileId: string) =>
  doc<PuzzleEntry>(`/puzzles/${puzzleId}/entries/${profileId}`);

export const championshipsRef = collection<Championship>("/championships");
export function getChampionshipRef(id: string) {
  return doc<Championship>(`/championships/${id}`);
}
export function getChampionshipTeamsRef(championship: string) {
  return collection<ChampionshipTeam>(`/championships/${championship}/competitors`);
}

export const sweepstakesRef = collection<Sweepstake>("/sweepstakes");
export const getSweepstakeRef = (id: string) => doc<Sweepstake>(`/sweepstakes/${id}`);
export const sweepstakeTeamsRef = (id: string) =>
  collection<SweepstakeTeam>(`/sweepstakes/${id}/teams`);

export const morsalsRef = collection<Morsal>("/morsals");
export const getMorsalRef = (id: string) => doc<Morsal>(`/morsals/${id}`);

export const quotesRef = collection<Quote>("/quotes");
export const quoteSessionRef = collection<QuoteRouletteSession>("/quoteRouletteSessions");
export const getQuoteRef = (id: string) => doc<Quote>(`/quotes/${id}`);

export const ledgerRef = collection<LedgerEntry>("/ledger");

export const coinEventsRef = collection<CoinEvent>("/coinEvents");
export const getLedgerEntryRef = (id: string) => doc<LedgerEntry>(`/ledger/${id}`);

export const awardsRef = collection<YearAwards>("/awards");
export const getAwardRef = (year: string | number) => doc<YearAwards>(`/awards/${year}`);

export const chatGroupsRef = collection<ChatGroup>("/chatGroups");
export const getChatGroupRef = (id: string) => doc<ChatGroup>(`/chatGroups/${id}`);
export const getChatMessagesRef = (groupId: string) =>
  collection<ChatMessage>(`/chatGroups/${groupId}/messages`);

export const broLoreRef = collection<BroBotMemory>("/broLore");
